import { create } from 'zustand';
import type {
  ExplorerTab,
  QueryResult,
  ColumnInfo,
  ColumnFormat,
  JoinItem,
  QueryHistoryEntry,
  PivotConfig,
} from '@/types/explorer';

interface ExplorerState {
  tabs: ExplorerTab[];
  activeTabId: string | null;

  reset: () => void;
  addTab: (name?: string, sql?: string) => string;
  duplicateTab: (id: string) => string;
  removeTab: (id: string) => void;
  setActiveTab: (id: string) => void;
  renameTab: (id: string, name: string) => void;
  updateTabSql: (id: string, sql: string) => void;
  setTabResult: (id: string, result: QueryResult | null) => void;
  setTabLoading: (id: string, loading: boolean) => void;
  setTabError: (id: string, error: string | null) => void;
  setTabView: (id: string, view: 'table' | 'chart') => void;
  setTabMode: (id: string, mode: 'visual' | 'sql') => void;
  setTabTable: (id: string, table: string | null) => void;
  setTabColumns: (id: string, columns: ColumnInfo[]) => void;
  setTabExecutionTime: (id: string, time: number | null) => void;
  setTabColumnFormat: (id: string, col: string, format: ColumnFormat) => void;
  clearTabColumnFormat: (id: string, col: string) => void;
  addAggregation: (id: string, col: string, fn: string) => void;
  removeAggregation: (id: string, index: number) => void;
  addGroupBy: (id: string, col: string, granularity?: string) => void;
  removeGroupBy: (id: string, col: string) => void;
  addFilter: (id: string, col: string, op: string, val: string) => void;
  removeFilter: (id: string, index: number) => void;
  updateAggregation: (id: string, index: number, col: string, fn: string) => void;
  updateGroupBy: (id: string, index: number, col: string, granularity?: string) => void;
  updateFilter: (id: string, index: number, col: string, op: string, val: string) => void;
  updateJoin: (id: string, joinId: string, join: JoinItem) => void;
  addJoin: (id: string, join: JoinItem) => void;
  removeJoin: (id: string, joinId: string) => void;
  setTabPivotConfig: (id: string, config: PivotConfig | undefined) => void;
  setTabColumnOrder: (id: string, order: string[]) => void;
  clearTabColumnOrder: (id: string) => void;
  clearAllTabColumnFormats: (id: string) => void;
  clearVisualBuilder: (id: string) => void;
  pushQueryHistory: (id: string, sql: string) => void;
  undoQuery: (id: string) => string | null;
  redoQuery: (id: string) => string | null;
}

const QUERY_HISTORY_MAX = 50;

function createTab(name?: string, sqlText?: string): ExplorerTab {
  return {
    id: crypto.randomUUID(),
    name: name || 'Query',
    sql: sqlText || '',
    result: null,
    view: 'table',
    mode: sqlText ? 'sql' : 'sql',
    table: null,
    columns: [],
    aggregations: [],
    groupBys: [],
    filters: [],
    joins: [],
    columnFormats: {},
    loading: false,
    error: null,
    executionTime: null,
    queryHistory: { entries: [], cursor: -1 },
  };
}

export const useExplorerStore = create<ExplorerState>((set, get) => {
  const initial = createTab('Query 1');
  return {
    tabs: [initial],
    activeTabId: initial.id,

    reset: () => {
      const tab = createTab('Query 1');
      set({ tabs: [tab], activeTabId: tab.id });
    },

    addTab: (name, sqlText) => {
      const tab = createTab(name || `Query ${get().tabs.length + 1}`, sqlText);
      set((s) => ({ tabs: [...s.tabs, tab], activeTabId: tab.id }));
      return tab.id;
    },

    duplicateTab: (id) => {
      const sourceHtml = get().tabs.find((t) => t.id === id);
      if (!sourceHtml) throw new Error('Tab not found');

      const newTab: ExplorerTab = {
        ...sourceHtml,
        id: crypto.randomUUID(),
        name: `${sourceHtml.name} (Copy)`,
        result: null, // Don't copy results
        loading: false,
        error: null,
        executionTime: null,
      };
      set((s) => ({ tabs: [...s.tabs, newTab], activeTabId: newTab.id }));
      return newTab.id;
    },

    removeTab: (id) => {
      set((s) => {
        const tabs = s.tabs.filter((t) => t.id !== id);
        if (tabs.length === 0) {
          const fresh = createTab('Query 1');
          return { tabs: [fresh], activeTabId: fresh.id };
        }
        const activeTabId =
          s.activeTabId === id
            ? tabs[Math.min(tabs.length - 1, Math.max(0, s.tabs.findIndex((t) => t.id === id) - 1))].id
            : s.activeTabId;
        return { tabs, activeTabId };
      });
    },

    setActiveTab: (id) => set({ activeTabId: id }),
    renameTab: (id, name) => set((s) => ({ tabs: s.tabs.map((t) => (t.id === id ? { ...t, name } : t)) })),
    updateTabSql: (id, sqlText) =>
      set((s) => ({ tabs: s.tabs.map((t) => (t.id === id ? { ...t, sql: sqlText } : t)) })),
    setTabResult: (id, result) => set((s) => ({ tabs: s.tabs.map((t) => (t.id === id ? { ...t, result } : t)) })),
    setTabLoading: (id, loading) => set((s) => ({ tabs: s.tabs.map((t) => (t.id === id ? { ...t, loading } : t)) })),
    setTabError: (id, error) => set((s) => ({ tabs: s.tabs.map((t) => (t.id === id ? { ...t, error } : t)) })),
    setTabView: (id, view) => set((s) => ({ tabs: s.tabs.map((t) => (t.id === id ? { ...t, view } : t)) })),
    setTabMode: (id, mode) => set((s) => ({ tabs: s.tabs.map((t) => (t.id === id ? { ...t, mode } : t)) })),
    setTabTable: (id, table) => set((s) => ({ tabs: s.tabs.map((t) => (t.id === id ? { ...t, table } : t)) })),
    setTabColumns: (id, columns) => set((s) => ({ tabs: s.tabs.map((t) => (t.id === id ? { ...t, columns } : t)) })),
    setTabPivotConfig: (id, config) =>
      set((s) => ({ tabs: s.tabs.map((t) => (t.id === id ? { ...t, pivotConfig: config } : t)) })),
    setTabExecutionTime: (id, time) =>
      set((s) => ({ tabs: s.tabs.map((t) => (t.id === id ? { ...t, executionTime: time } : t)) })),
    setTabColumnFormat: (id, col, format) =>
      set((s) => ({
        tabs: s.tabs.map((t) => (t.id === id ? { ...t, columnFormats: { ...t.columnFormats, [col]: format } } : t)),
      })),
    clearTabColumnFormat: (id, col) =>
      set((s) => ({
        tabs: s.tabs.map((t) => {
          if (t.id !== id) return t;
          const { [col]: _, ...rest } = t.columnFormats;
          return { ...t, columnFormats: rest };
        }),
      })),

    addGroupBy: (id, col, granularity) =>
      set((s) => ({
        tabs: s.tabs.map((t) =>
          t.id === id ? { ...t, groupBys: [...t.groupBys, { col, ...(granularity ? { granularity } : {}) }] } : t,
        ),
      })),

    removeGroupBy: (id, col) =>
      set((s) => ({
        tabs: s.tabs.map((t) => (t.id === id ? { ...t, groupBys: t.groupBys.filter((g) => g.col !== col) } : t)),
      })),

    addFilter: (id, col, op, val) =>
      set((s) => ({
        tabs: s.tabs.map((t) => (t.id === id ? { ...t, filters: [...t.filters, { col, op, val }] } : t)),
      })),

    removeFilter: (id, index) =>
      set((s) => ({
        tabs: s.tabs.map((t) => (t.id === id ? { ...t, filters: t.filters.filter((_, i) => i !== index) } : t)),
      })),

    addAggregation: (id, col, fn) =>
      set((s) => ({
        tabs: s.tabs.map((t) => (t.id === id ? { ...t, aggregations: [...t.aggregations, { col, fn }] } : t)),
      })),

    removeAggregation: (id, index) =>
      set((s) => ({
        tabs: s.tabs.map((t) =>
          t.id === id ? { ...t, aggregations: t.aggregations.filter((_, i) => i !== index) } : t,
        ),
      })),

    updateAggregation: (id, index, col, fn) =>
      set((s) => ({
        tabs: s.tabs.map((t) =>
          t.id === id
            ? {
                ...t,
                aggregations: t.aggregations.map((agg, i) => (i === index ? { col, fn } : agg)),
              }
            : t,
        ),
      })),

    updateGroupBy: (id, index, col, granularity) =>
      set((s) => ({
        tabs: s.tabs.map((t) =>
          t.id === id
            ? {
                ...t,
                groupBys: t.groupBys.map((g, i) => (i === index ? { col, granularity } : g)),
              }
            : t,
        ),
      })),

    updateFilter: (id, index, col, op, val) =>
      set((s) => ({
        tabs: s.tabs.map((t) =>
          t.id === id
            ? {
                ...t,
                filters: t.filters.map((f, i) => (i === index ? { col, op, val } : f)),
              }
            : t,
        ),
      })),

    updateJoin: (id, joinId, join) =>
      set((s) => ({
        tabs: s.tabs.map((t) =>
          t.id === id
            ? {
                ...t,
                joins: t.joins.map((j) => (j.id === joinId ? join : j)),
              }
            : t,
        ),
      })),

    addJoin: (id, join) =>
      set((s) => ({
        tabs: s.tabs.map((t) => (t.id === id ? { ...t, joins: [...t.joins, join] } : t)),
      })),

    removeJoin: (id, joinId) =>
      set((s) => ({
        tabs: s.tabs.map((t) => (t.id === id ? { ...t, joins: t.joins.filter((j) => j.id !== joinId) } : t)),
      })),

    setTabColumnOrder: (id, order) =>
      set((s) => ({
        tabs: s.tabs.map((t) => (t.id === id ? { ...t, columnOrder: order } : t)),
      })),

    clearTabColumnOrder: (id) =>
      set((s) => ({
        tabs: s.tabs.map((t) => (t.id === id ? { ...t, columnOrder: undefined } : t)),
      })),

    clearAllTabColumnFormats: (id) =>
      set((s) => ({
        tabs: s.tabs.map((t) => (t.id === id ? { ...t, columnFormats: {} } : t)),
      })),

    clearVisualBuilder: (id) =>
      set((s) => ({
        tabs: s.tabs.map((t) =>
          t.id === id ? { ...t, aggregations: [], groupBys: [], filters: [], joins: [], columnFormats: {} } : t,
        ),
      })),

    pushQueryHistory: (id, sql) =>
      set((s) => ({
        tabs: s.tabs.map((t) => {
          if (t.id !== id) return t;
          const h = t.queryHistory;
          const trimmed = sql.trim();
          // Dedup: skip if SQL is identical to current entry
          if (h.cursor >= 0 && h.entries[h.cursor].sql === trimmed) return t;
          // Snapshot current visual builder state
          const entry: QueryHistoryEntry = {
            sql: trimmed,
            mode: t.mode,
            aggregations: [...t.aggregations],
            groupBys: [...t.groupBys],
            filters: [...t.filters],
            joins: [...t.joins],
            columnFormats: { ...t.columnFormats },
            columnOrder: t.columnOrder ? [...t.columnOrder] : undefined,
          };
          const base = h.entries.slice(0, h.cursor + 1);
          const next = [...base, entry];
          const capped = next.length > QUERY_HISTORY_MAX ? next.slice(next.length - QUERY_HISTORY_MAX) : next;
          return { ...t, queryHistory: { entries: capped, cursor: capped.length - 1 } };
        }),
      })),

    undoQuery: (id) => {
      const tab = get().tabs.find((t) => t.id === id);
      if (!tab) return null;
      const h = tab.queryHistory;
      if (h.entries.length === 0) return null;
      if (h.cursor <= 0) {
        const entry = h.entries[0];
        if (tab.sql.trim() === entry.sql) return null;
        set((s) => ({
          tabs: s.tabs.map((t) =>
            t.id === id
              ? {
                  ...t,
                  sql: entry.sql,
                  mode: entry.mode,
                  aggregations: entry.aggregations,
                  groupBys: entry.groupBys,
                  filters: entry.filters,
                  joins: entry.joins,
                  columnFormats: entry.columnFormats,
                  columnOrder: entry.columnOrder,
                  queryHistory: { ...t.queryHistory, cursor: 0 },
                }
              : t,
          ),
        }));
        return entry.sql;
      }
      const newCursor = h.cursor - 1;
      const entry = h.entries[newCursor];
      set((s) => ({
        tabs: s.tabs.map((t) =>
          t.id === id
            ? {
                ...t,
                sql: entry.sql,
                mode: entry.mode,
                aggregations: entry.aggregations,
                groupBys: entry.groupBys,
                filters: entry.filters,
                joins: entry.joins,
                columnFormats: entry.columnFormats,
                columnOrder: entry.columnOrder,
                queryHistory: { ...t.queryHistory, cursor: newCursor },
              }
            : t,
        ),
      }));
      return entry.sql;
    },

    redoQuery: (id) => {
      const tab = get().tabs.find((t) => t.id === id);
      if (!tab) return null;
      const h = tab.queryHistory;
      if (h.cursor >= h.entries.length - 1) return null;
      const newCursor = h.cursor + 1;
      const entry = h.entries[newCursor];
      set((s) => ({
        tabs: s.tabs.map((t) =>
          t.id === id
            ? {
                ...t,
                sql: entry.sql,
                mode: entry.mode,
                aggregations: entry.aggregations,
                groupBys: entry.groupBys,
                filters: entry.filters,
                joins: entry.joins,
                columnFormats: entry.columnFormats,
                columnOrder: entry.columnOrder,
                queryHistory: { ...t.queryHistory, cursor: newCursor },
              }
            : t,
        ),
      }));
      return entry.sql;
    },
  };
});
